import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { CatalogItem } from '../models/reel.model';

/**
 * The end-card catalog marquee — an infinitely auto-scrolling row of cards.
 * Ported from iOS `CatalogMarquee` (`ReelSubviews.swift`): card 110×70, spacing 14, radius 11,
 * base fill white@0.1, velocity 30 pt/s. Time-driven via CSS so it costs no change-detection.
 * Paused under `prefers-reduced-motion`.
 */
@Component({
    selector: 'app-reel-marquee',
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
    <div class="mq" *ngIf="items?.length">
      <div class="mq-track"
           [style.--mq-shift]="shiftPx + 'px'"
           [style.animation-duration]="durationSeconds + 's'">
        <ng-container *ngFor="let copy of copies">
          <div class="mq-card" *ngFor="let item of items; trackBy: trackByIndex">
            <img class="mq-img" [src]="item.imageUrl" alt="" />
          </div>
        </ng-container>
      </div>
    </div>
  `,
    styles: [`
    .mq { overflow: hidden; width: 100%; }
    .mq-track {
      display: flex;
      width: max-content;
      animation-name: mq-scroll;
      animation-timing-function: linear;
      animation-iteration-count: infinite;
      will-change: transform;
    }
    .mq-card {
      width: 110px;
      height: 70px;
      margin-right: 14px;
      border-radius: 11px;
      overflow: hidden;
      background: rgba(255, 255, 255, 0.1);
      flex: 0 0 auto;
    }
    .mq-img { width: 100%; height: 100%; object-fit: cover; display: block; }
    @keyframes mq-scroll {
      from { transform: translateX(0); }
      to { transform: translateX(calc(-1 * var(--mq-shift, 0px))); }
    }
    @media (prefers-reduced-motion: reduce) {
      .mq-track { animation: none; }
    }
  `],
    standalone: false
})
export class ReelMarqueeComponent {
  @Input() items: CatalogItem[] = [];

  /** One copy's width: (110 card + 14 gap) per card. The keyframe shifts by exactly this. */
  get shiftPx(): number {
    return 124 * (this.items?.length ?? 0);
  }

  /** Enough copies that the strip always overflows the widest reel column (≤480px) with no gap. */
  get copies(): number[] {
    const rw = this.shiftPx;
    if (rw <= 0) return [];
    const n = Math.max(2, Math.ceil(1 + 480 / rw));
    return Array.from({ length: n }, (_, i) => i);
  }

  /** Traverse one copy width at 30 pt/s. */
  get durationSeconds(): number {
    return this.shiftPx > 0 ? this.shiftPx / 30 : 0;
  }

  trackByIndex(i: number): number {
    return i;
  }
}
